import React from 'react';
import type { JSONContent } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { generateJSON } from '@tiptap/html';
import { renderToReactElement } from '@tiptap/static-renderer';

/**
 * Extension schema used to render stored rich-text HTML read-only. It mirrors the
 * set configured in RichTextEditor so parsing/rendering matches how the content
 * was authored (StarterKit's bundled Link is disabled in favor of our own
 * configured Link, same as the editor). Exported so other read-only renderers can
 * share the exact schema.
 */
export const READONLY_RICH_TEXT_EXTENSIONS = [
    StarterKit.configure({ link: false }),
    Highlight,
    TextStyle,
    Color,
    Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
];

interface RichTextContentProps {
    /**
     * Tiptap ProseMirror JSON (preferred). When present it's rendered directly,
     * skipping the HTML parse. Typically the stored `bodyJson`.
     */
    json?: JSONContent | null;
    /**
     * HTML string produced by RichTextEditor (Tiptap getHTML output). Used as a
     * fallback for legacy records that only have HTML. Typically the stored `body`.
     */
    html?: string;
}

/** A valid ProseMirror JSON doc must be an object with a `type`. */
function isRenderableJson(value: unknown): value is JSONContent {
    return !!value && typeof value === 'object' && typeof (value as JSONContent).type === 'string';
}

/**
 * Renders Tiptap-authored content as React elements — no dangerouslySetInnerHTML.
 *
 * Accepts either ProseMirror JSON (`json`, preferred) or an HTML string (`html`,
 * legacy fallback). JSON is rendered directly; HTML is first parsed to JSON with
 * the same extension schema the editor uses. Rendering is constrained to the
 * known schema (only these nodes/marks) via @tiptap/static-renderer, so
 * arbitrary/unsafe markup can't be injected, and no editor instance is needed.
 *
 * Returns a fragment of rendered nodes — wrap it in your own container/class.
 */
const RichTextContent: React.FC<RichTextContentProps> = ({ json, html }) => {
    const content = React.useMemo(() => {
        // Prefer JSON when it's a valid doc.
        if (isRenderableJson(json)) {
            return json;
        }
        // Fall back to parsing the HTML string (legacy records).
        try {
            return generateJSON(html || '', READONLY_RICH_TEXT_EXTENSIONS);
        } catch {
            // Malformed content shouldn't crash the caller; render nothing.
            return { type: 'doc', content: [] };
        }
    }, [json, html]);

    return <>{renderToReactElement({ content, extensions: READONLY_RICH_TEXT_EXTENSIONS })}</>;
};

export default RichTextContent;
